// /pair — what a phone opens when it scans a device's QR code (card 540f21a4,
// Devin 2026-10-25): the device's pairing code, and the events the signed-in
// organizer (or admin) can pair devices for now. Picking one opens its Event
// Devices screen with the code filled in, to choose the job and a name.
// Signed out: sign in as the organizer and come back here (the code waits in
// sessionStorage), or open the admin app. Plain fetch (deviceApi), so a
// parent's account refused here isn't signed out.
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Msr, useMaterialSymbols } from '../Teacher/Oireachtas/components';
import { openEvents, pairSites } from './deviceApi';
import { rememberOrganizerReturnPath } from '../Organizer/Oireachtas/panelLink';
import '../Teacher/Oireachtas/oireachtas.css';
import '../Organizer/Oireachtas/organizerOireachtas.css';
import './devicePair.css';

const CODE_KEY = 'feislink.pair.code';
const digitsOf = (v) => {
  const d = String(v || '').replace(/[^0-9]/g, '');
  return d.length === 6 ? d : '';
};

// The code in the address, else the one kept while signing in.
export function pairCodeFrom(search) {
  const fromUrl = digitsOf(new URLSearchParams(search).get('code'));
  try {
    if (fromUrl) sessionStorage.setItem(CODE_KEY, fromUrl);
    return fromUrl || digitsOf(sessionStorage.getItem(CODE_KEY));
  } catch (e) {
    return fromUrl;
  }
}
const forget = () => {
  try {
    sessionStorage.removeItem(CODE_KEY);
  } catch (e) {
    // nothing kept
  }
};
// An ended sign-in (expired or invalid token) as the API reports it: 401, or a
// 403 that says so; a parent's account is a different 403 (panelLink.js).
const SESSION_ENDED = /session expired|invalid token|error finding user/i;
const sessionEnded = (e) => e.status === 401 || (e.status === 403 && SESSION_ENDED.test(String(e.message || '')));

const shown = (d) => (d ? `${d.slice(0, 3)}-${d.slice(3)}` : '');
const dayText = (day) =>
  day ? new Date(`${day}T12:00:00Z`).toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' }) : '';

const PairScan = () => {
  useMaterialSymbols();
  const navigate = useNavigate();
  const location = useLocation();
  const [code] = useState(() => pairCodeFrom(location.search));
  // an ended sign-in is dropped here, so the page offers sign-in again with
  // the code kept (web MC review a7a4b3cc R1-F2)
  const [expired, setExpired] = useState(false);
  const token = expired ? null : localStorage.getItem('accessToken');
  const [events, setEvents] = useState(null);
  const [error, setError] = useState('');
  const [notStaff, setNotStaff] = useState(false);
  const [admin, setAdmin] = useState('');

  useEffect(() => {
    pairSites()
      .then((r) => setAdmin(r.admin || ''))
      .catch(() => {});
    if (!token) return;
    openEvents(token)
      .then((r) => setEvents(r.events || []))
      .catch((e) => {
        if (sessionEnded(e)) {
          localStorage.removeItem('accessToken');
          setExpired(true);
        } else if (e.status === 403) setNotStaff(true);
        else setError(e.offline ? 'No connection — check the network and try again.' : e.message);
      });
  }, [token]);

  const signIn = () => {
    rememberOrganizerReturnPath('/pair');
    navigate('/login-org');
  };
  const open = (ev) => {
    forget();
    navigate(`/job-devices?id=${ev._id}${ev.kind === 'oireachtas' ? '&kind=oireachtas' : ''}&code=${code}`);
  };
  const adminLink = admin && code ? `${admin}/pair?code=${code}` : '';
  const adminLine = adminLink ? (
    <p className="dp-alt">
      Feis.link admin? <a href={adminLink}>Open this in the admin app</a>
    </p>
  ) : null;

  let body;
  if (!code) {
    body = <p className="dp-error">This QR code has no pairing code in it. Scan the code on the device again.</p>;
  } else if (!token) {
    body = (
      <>
        <p className="dp-hint">
          {expired ? 'Your sign-in has ended. ' : ''}Sign in with the event's organiser account to choose the event and the device's job.
        </p>
        <button type="button" className="op-btn dp-go" onClick={signIn}>
          Sign in as organiser
        </button>
        {adminLine}
      </>
    );
  } else if (notStaff) {
    body = (
      <>
        <p className="dp-hint">This account can't pair devices. Sign out, then sign in with the event's organiser account.</p>
        {adminLine}
      </>
    );
  } else if (error) {
    body = <p className="dp-error">{error}</p>;
  } else if (events === null) {
    body = <p className="dp-hint">Loading your events…</p>;
  } else if (!events.length) {
    body = (
      <>
        <p className="dp-hint">None of your events is open for pairing now. Devices can be paired from the day before an event until it ends.</p>
        {adminLine}
      </>
    );
  } else {
    body = (
      <>
        <p className="dp-hint">Which event is this device for?</p>
        <div className="dp-events">
          {events.map((ev) => (
            <button key={ev._id} type="button" className="dp-event" onClick={() => open(ev)}>
              <b>{ev.name}</b>
              <span>{dayText(ev.day)}</span>
              <Msr>chevron_right</Msr>
            </button>
          ))}
        </div>
        {adminLine}
      </>
    );
  }

  return (
    <div className="oir dp-page">
      <main className="dp-card" aria-live="polite">
        <h1>Pair a device</h1>
        {code ? (
          <div className="dp-code dp-code-sm" data-testid="pair-code">
            {shown(code)}
          </div>
        ) : null}
        {body}
      </main>
    </div>
  );
};

export default PairScan;
